import { toast } from '../../../components/ui/toaster';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import type {
  SecurityManagerTabContent,
  SecurityManagerView,
  SecurityObjectType,
} from '../../workspace/core/types/workspaceTypes';
import { getConnectionContext } from './securityExplorerNodeContext';
import { getEngineForConnection } from '../../connections/utils/connectionLookup';
import type { DatabaseEngineId } from '../../../core/engines/engineTypes';

function getEngineForConnectionTyped(connectionId: string): DatabaseEngineId {
  return (getEngineForConnection(connectionId) as DatabaseEngineId) || 'mssql';
}

function getDefaultObjectType(engine: DatabaseEngineId, view: SecurityManagerView): SecurityObjectType {
  if (engine === 'oracle') {
    return 'oracle-user';
  }
  if (engine === 'mysql') {
    return 'mysql-user';
  }
  if (engine === 'pgsql') {
    return view === 'database' ? 'schema' : 'login-role';
  }
  return view === 'database' ? 'user' : 'login';
}

export function openSecurityManagerFromExplorerFolder(options: {
  connectionId: string;
  view: SecurityManagerView;
  database?: string;
  objectType?: SecurityObjectType;
}): void {
  const { connectionId, view, database, objectType } = options;

  const connectionContext = getConnectionContext(connectionId);
  if (!connectionContext) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  if (view === 'database' && !database) {
    toast.error({ title: 'Error', description: 'Database is required for this action' });
    return;
  }

  const { tabs, addTab, setActiveTab, updateTabContent, updateTabConnectionInfo, updateTabTitle } =
    useWorkspaceStore.getState();

  const engine = getEngineForConnectionTyped(connectionId);

  const content: Partial<SecurityManagerTabContent> = {
    view,
    objectType: objectType ?? getDefaultObjectType(engine, view),
    selectedDatabase: view === 'database' ? database : undefined,
    selectedObjectId: undefined,
    isCreateMode: false,
  };

  const connectionLabel =
    connectionContext.connectionName === 'Unnamed Connection'
      ? connectionContext.serverHost
      : connectionContext.connectionName;

  const title = `Security Manager - ${connectionLabel}`;
  const connectionInfo = {
    connectionId,
    connectionName: connectionContext.connectionName,
    serverHost: connectionContext.serverHost,
    database: view === 'database' ? database : connectionContext.defaultDatabase,
  };

  const existingTab = tabs.find(
    (tab) => tab.type === 'security' && tab.connectionInfo?.connectionId === connectionId
  );

  if (existingTab) {
    updateTabContent(existingTab.id, content);
    updateTabConnectionInfo(existingTab.id, connectionInfo);
    if (existingTab.customTitle !== true) {
      updateTabTitle(existingTab.id, title);
    }
    setActiveTab(existingTab.id);
    return;
  }

  addTab({ type: 'security', title, connectionInfo, content });
}
